/**
 * Daily quests and weekly goals.
 *
 * The streak answers "did you come back?". It says nothing about *what* was
 * done when you got here, and a student optimising purely for the flame learns
 * to answer one easy question a day. Quests are the counterweight: three small,
 * concrete goals that change every day and ask for slightly different work —
 * some volume, some accuracy, some difficulty, some review.
 *
 * Three rules shape the design:
 *
 * 1. **Reachable inside the free daily cap.** The heaviest quest asks for
 *    twelve questions against a thirty-question free allowance, so a quest can
 *    never push a child into the paywall to finish it.
 * 2. **Never a punishment.** A quest not finished expires quietly. Nothing is
 *    lost, no XP is deducted, and yesterday's failure is not mentioned today.
 * 3. **Deterministic.** Which three quests today holds is a hash of the learner
 *    id and the local day, so it is stable across a reload and across devices
 *    without a server round trip — and, like everything else in this package,
 *    it is testable by passing a different `now` rather than a different roll.
 *
 * Pure, `now` injected, no randomness. See `docs/development.md`.
 */

import { dueConcepts } from './review.ts';
import { activeDaysInWindow, dayKey, DAILY_QUEST_COUNT } from './progress.ts';
import type { DailyProgress, LearnerProfile } from './types.ts';

/* -------------------------------------------------------------------------- */
/* Today's counters                                                           */
/* -------------------------------------------------------------------------- */

export function emptyDaily(day: string, questIds: string[] = []): DailyProgress {
  return {
    day,
    attempts: 0,
    correct: 0,
    hardCorrect: 0,
    unaidedCorrect: 0,
    reviewAttempts: 0,
    lessonsCompleted: 0,
    combo: 0,
    bestCombo: 0,
    conceptIds: [],
    questIds,
    claimedQuestIds: [],
  };
}

/**
 * Today's record, starting a new one when the local day has turned.
 *
 * Reading rather than writing: callers that only want to *display* today's
 * progress get a correct answer without having to mutate the profile first.
 * The day's quests are chosen here, once, and then travel with the record.
 */
export function rollDaily(profile: LearnerProfile, now: number): DailyProgress {
  const today = dayKey(now);
  const stored = profile.daily;
  if (stored && stored.day === today) {
    // A record written before quests were pinned, or by an older client.
    return stored.questIds?.length ? stored : { ...stored, questIds: chooseQuests(profile, now).map((q) => q.id) };
  }
  return emptyDaily(today, chooseQuests(profile, now).map((item) => item.id));
}

/* -------------------------------------------------------------------------- */
/* The quest pool                                                             */
/* -------------------------------------------------------------------------- */

export type Quest = {
  id: string;
  title: string;
  /** One line, addressed to the student. */
  description: string;
  icon: string;
  target: number;
  /** Bonus XP paid once, the moment the target is met. */
  xp: number;
  /**
   * Guarantees at least one quest a beginner can finish with plain practice.
   * Exactly one is offered each day.
   */
  staple?: boolean;
  /** Progress towards `target`. Must be monotonic — a bar may not run backwards. */
  measure(daily: DailyProgress): number;
  /**
   * Whether this quest can be finished at all today. A review quest offered to
   * a student with nothing due is an impossible task dressed as a goal.
   */
  offerable?(profile: LearnerProfile, now: number): boolean;
};

export const QUEST_POOL: Quest[] = [
  {
    id: 'warm-up',
    title: 'Warm up',
    description: 'Answer five questions.',
    icon: '🌅',
    target: 5,
    xp: 15,
    staple: true,
    measure: (daily) => daily.attempts,
  },
  {
    id: 'session',
    title: 'A proper sitting',
    description: 'Answer twelve questions.',
    icon: '📈',
    target: 12,
    xp: 30,
    staple: true,
    measure: (daily) => daily.attempts,
  },
  {
    id: 'sharp',
    title: 'Sharp today',
    description: 'Get eight answers right.',
    icon: '🎯',
    target: 8,
    xp: 25,
    measure: (daily) => daily.correct,
  },
  {
    id: 'unaided',
    title: 'On your own',
    description: 'Get six right first time, with no hint.',
    icon: '🧠',
    target: 6,
    xp: 30,
    measure: (daily) => daily.unaidedCorrect,
  },
  {
    id: 'deep-end',
    title: 'The deep end',
    description: 'Get three hard questions right.',
    icon: '⛏️',
    target: 3,
    xp: 30,
    measure: (daily) => daily.hardCorrect,
  },
  {
    id: 'combo',
    title: 'Five in a row',
    description: 'Answer five in a row correctly.',
    icon: '🎳',
    target: 5,
    xp: 25,
    measure: (daily) => daily.bestCombo,
  },
  {
    id: 'remember',
    title: 'Nothing forgotten',
    description: 'Answer four questions from your review queue.',
    icon: '🔁',
    target: 4,
    xp: 30,
    measure: (daily) => daily.reviewAttempts,
    offerable: (profile, now) => dueConcepts(profile.concepts, now).length > 0,
  },
  {
    id: 'read',
    title: 'Read something new',
    description: 'Finish a lesson.',
    icon: '📖',
    target: 1,
    xp: 25,
    measure: (daily) => daily.lessonsCompleted,
  },
  {
    id: 'two-fronts',
    title: 'Two fronts',
    description: 'Practise two different concepts.',
    icon: '🧭',
    target: 2,
    xp: 20,
    measure: (daily) => daily.conceptIds.length,
  },
];

const QUEST_BY_ID = new Map(QUEST_POOL.map((quest) => [quest.id, quest]));

export function quest(id: string): Quest | undefined {
  return QUEST_BY_ID.get(id);
}

/** Paid on top when every quest in a day is finished. */
export const QUEST_SWEEP_BONUS_XP = 25;

/* -------------------------------------------------------------------------- */
/* Choosing the day's quests                                                  */
/* -------------------------------------------------------------------------- */

/** FNV-1a. Small, stable across engines, and good enough to shuffle a list. */
function hash(text: string): number {
  let value = 2_166_136_261;
  for (let i = 0; i < text.length; i += 1) {
    value ^= text.charCodeAt(i);
    value = Math.imul(value, 16_777_619);
  }
  return value >>> 0;
}

/**
 * The three quests for one learner on one day.
 *
 * Always exactly one staple, so there is always something a student who simply
 * practises will finish; the other two are drawn from what is actually
 * achievable for this profile today. Called once per day, by `rollDaily` —
 * read the pinned list with `questsForDay` instead.
 */
export function chooseQuests(profile: LearnerProfile, now: number): Quest[] {
  const seed = hash(`${profile.id}:${dayKey(now)}`);
  const offerable = (item: Quest): boolean => item.offerable?.(profile, now) ?? true;

  const staples = QUEST_POOL.filter((item) => item.staple && offerable(item));
  const rest = QUEST_POOL.filter((item) => !item.staple && offerable(item));

  const chosen: Quest[] = [];
  const first = staples[seed % Math.max(1, staples.length)];
  if (first) chosen.push(first);

  // Ordered by a per-quest hash rather than picked repeatedly from a shrinking
  // list: sorting a set cannot yield the same entry twice, which is what keeps
  // "three quests" from ever quietly being two.
  const shuffled = [...rest].sort((a, b) => hash(`${a.id}:${seed}`) - hash(`${b.id}:${seed}`));
  for (const item of shuffled) {
    if (chosen.length >= DAILY_QUEST_COUNT) break;
    chosen.push(item);
  }
  return chosen;
}

/** Resolves a pinned quest list back to the quests themselves. */
function questsOf(daily: DailyProgress): Quest[] {
  return daily.questIds.map((id) => QUEST_BY_ID.get(id)).filter((item): item is Quest => item !== undefined);
}

/** The quests today is actually carrying, in the order they were pinned. */
export function questsForDay(profile: LearnerProfile, now: number): Quest[] {
  return rollDaily(profile, now)
    .questIds.map((id) => QUEST_BY_ID.get(id))
    .filter((item): item is Quest => item !== undefined);
}

export type QuestStatus = {
  quest: Quest;
  current: number;
  target: number;
  fraction: number;
  complete: boolean;
  /** The reward has been paid. Equal to `complete` except within one reducer step. */
  claimed: boolean;
};

/** Today's quests with progress filled in, for display. */
export function dailyQuests(profile: LearnerProfile, now: number): QuestStatus[] {
  const daily = rollDaily(profile, now);
  return questsOf(daily).map((item) => {
    const current = Math.min(item.target, item.measure(daily));
    return {
      quest: item,
      current,
      target: item.target,
      fraction: item.target === 0 ? 1 : current / item.target,
      complete: current >= item.target,
      claimed: daily.claimedQuestIds.includes(item.id),
    };
  });
}

export type QuestSettlement = {
  daily: DailyProgress;
  /** Quests finished by the change that was just applied. */
  completed: Quest[];
  /** Bonus XP owed for them, including the clean-sweep bonus. */
  xp: number;
  /** True when this settlement finished the last outstanding quest of the day. */
  sweptTheDay: boolean;
};

/**
 * Pays for any quest whose target has just been met.
 *
 * Idempotent by construction: a quest id in `claimedQuestIds` is never paid
 * again, so calling this after every attempt costs nothing and cannot be made
 * to double-pay by a retry, a merge, or a clock that moves backwards.
 */
export function settleQuests(daily: DailyProgress): QuestSettlement {
  const today = questsOf(daily);
  const completed: Quest[] = [];

  for (const item of today) {
    if (daily.claimedQuestIds.includes(item.id)) continue;
    if (item.measure(daily) >= item.target) completed.push(item);
  }

  if (completed.length === 0) {
    return { daily, completed: [], xp: 0, sweptTheDay: false };
  }

  const claimedQuestIds = [...daily.claimedQuestIds, ...completed.map((item) => item.id)];
  const sweptTheDay = today.length > 0 && today.every((item) => claimedQuestIds.includes(item.id));
  const xp = completed.reduce((sum, item) => sum + item.xp, 0) + (sweptTheDay ? QUEST_SWEEP_BONUS_XP : 0);

  return { daily: { ...daily, claimedQuestIds }, completed, xp, sweptTheDay };
}

/* -------------------------------------------------------------------------- */
/* The weekly goal                                                            */
/* -------------------------------------------------------------------------- */

export const DEFAULT_WEEKLY_GOAL_DAYS = 5;

export type WeeklyGoal = {
  /** Days a week the student is aiming for. Their number, not ours. */
  target: number;
  /** Days practised in the last seven, oldest first. */
  days: string[];
  daysActive: number;
  fraction: number;
  complete: boolean;
};

/**
 * Progress against the student's own weekly target.
 *
 * A rolling seven days rather than a calendar week, deliberately. A calendar
 * week resets the count to zero on Monday morning, which is exactly when a
 * student who practised Thursday, Friday and Saturday most needs to see that
 * the work counted. The rolling window never throws away a good run because a
 * date rolled over.
 */
export function weeklyGoal(profile: LearnerProfile, now: number): WeeklyGoal {
  const target = Math.max(1, Math.min(7, Math.round(profile.weeklyGoalDays ?? DEFAULT_WEEKLY_GOAL_DAYS)));
  const days = activeDaysInWindow(profile, now, 7);
  return {
    target,
    days,
    daysActive: days.length,
    fraction: Math.min(1, days.length / target),
    complete: days.length >= target,
  };
}
